import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import Reveal, { SectionHeading } from "@/components/Reveal";
import api, { formatApiError } from "@/lib/api";

const PROJECT_TYPES = ["Portrait", "Couple", "Famille", "Studio", "Projet personnel", "Événement", "Mise en valeur professionnelle", "Nature / animaux", "Autre"];

const initial = { nom: "", courriel: "", telephone: "", type_projet: "Portrait", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = (e) => {
  e.preventDefault();

  const subject = encodeURIComponent(
    `Nouveau message - ${form.type_projet} - ${form.nom}`
  );

  const body = encodeURIComponent(
    `Nom : ${form.nom}
Courriel : ${form.courriel}
Téléphone : ${form.telephone || "-"}
Type de projet : ${form.type_projet}

Message :
${form.message}`
  );

  window.location.href =
   `mailto:photographedanieldulude@gmail.com?subject=${subject}&body=${body}`;
};

  const inputClass = "w-full bg-[#121216] border border-[#D4AF37]/20 focus:border-[#D4AF37]/70 outline-none px-5 py-4 text-sm text-[#F8F9FA] placeholder:text-slate-600 transition-colors duration-300";

  return (
    <main data-testid="contact-page" className="pt-20">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <SectionHeading number="01" eyebrow="Contact" title="Contactez-moi"
                        subtitle="Parlons de votre projet. Il me fera plaisir de répondre à vos questions." />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 mt-16">
          <Reveal className="lg:col-span-3">
            <form onSubmit={submit} className="gold-card p-8 sm:p-10 space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="nom" className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] block mb-2">Nom</label>
                  <input id="nom" data-testid="contact-name-input" required value={form.nom} onChange={set("nom")} placeholder="Votre nom" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="courriel" className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] block mb-2">Courriel</label>
                  <input id="courriel" type="email" data-testid="contact-email-input" required value={form.courriel} onChange={set("courriel")} placeholder="vous@exemple.ca" className={inputClass} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="telephone" className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] block mb-2">Téléphone</label>
                  <input id="telephone" data-testid="contact-phone-input" value={form.telephone} onChange={set("telephone")} placeholder="514-000-0000" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="type_projet" className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] block mb-2">Type de projet</label>
                  <select id="type_projet" data-testid="contact-project-type-select" value={form.type_projet} onChange={set("type_projet")} className={inputClass}>
                    {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="message" className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] block mb-2">Message</label>
                <textarea id="message" data-testid="contact-message-textarea" required rows={6} value={form.message} onChange={set("message")}
                          placeholder="Parlez-moi de votre projet, de vos idées, de vos envies…" className={`${inputClass} resize-none`} />
              </div>
              <button type="submit" data-testid="contact-submit-button" disabled={sending}
                      className="btn-gold w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed">
                {sending ? "Envoi en cours…" : "Envoyer"} <Send size={14} />
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-2">
            <div className="space-y-6">
              <div className="gold-card p-8" data-testid="contact-info-card">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-6">Coordonnées</p>
                <div className="space-y-5 text-sm">
                  <a href="tel:+15147818360" data-testid="contact-phone-link" className="flex items-center gap-4 text-slate-200 hover:text-[#F3E5AB] transition-colors">
                    <Phone size={17} className="text-[#D4AF37] shrink-0" />
                    <span>514-781-8360 <span className="text-slate-500 block text-xs mt-1">Appel ou texto</span></span>
                  </a>
                 <a href="mailto:photographedanieldulude@gmail.com">
                    <Mail size={17} className="text-[#D4AF37] shrink-0" /> photographedanieldulude@gmail.com
                  </a>
                 <p className="flex items-center gap-4 text-slate-300">
  <span>
    Lundi au vendredi : 10 h 00 à 21 h 00
    <span className="text-slate-500 block text-xs mt-1">
      Samedi : 10 h 00 à 17 h 00
      <br />
      Dimanche : Fermé — journée relaxe 😎
    </span>
  </span>
</p>
                 
                  <p className="flex items-center gap-4 text-slate-300">
                    <MapPin size={17} className="text-[#D4AF37] shrink-0" /> Saint-Jean-sur-Richelieu, Montérégie, Québec, Canada
                  </p>
                </div>
              </div>
              <div className="gold-card p-8" data-testid="contact-travel-fees">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-4">Frais de déplacement</p>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Aucun frais d'essence dans mon secteur et les environs. Pour une destination plus éloignée, des frais
                  peuvent s'appliquer selon la distance — nous en discuterons ensemble avant la séance.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
